import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import { vendorpayApi } from "../api/vendorpayApi";
import { isIndianMobile10, sanitizeIndianMobile10Input } from "../../util/indianMobile10";
import "../events.css";
import "./stall-order.css";

/**
 * Customer stall storefront — opened from a QR short link
 * (``…/s/{code}`` → ``/events/stall-order?event_id=&stall_id=``).
 *
 * Flow: browse menu → cart → name & mobile → place order → pay with Cashfree
 * → poll until paid → show receipt + order QR for pickup.
 *
 * UX goals:
 *   - Mobile-first: menu grid on top, floating "view order" CTA at the
 *     bottom that opens a bottom-sheet.
 *   - Desktop: menu + sticky order sidebar.
 *   - Clear GST split (CGST 9% + SGST 9%) on every money-facing surface.
 *   - Inline +/- stepper on each item card so the customer never has to
 *     tap a number input to change quantity.
 */

function loadCashfreeScript() {
  return new Promise(function (resolve, reject) {
    if (typeof window === "undefined") return resolve();
    if (window.Cashfree) return resolve();
    var s = document.createElement("script");
    s.src = "https://sdk.cashfree.com/js/v3/cashfree.js";
    s.async = true;
    s.onload = function () { resolve(); };
    s.onerror = function () { reject(new Error("Could not load payment SDK")); };
    document.body.appendChild(s);
  });
}

function useQuery() {
  var loc = useLocation();
  return useMemo(function () {
    return new URLSearchParams(loc.search || "");
  }, [loc.search]);
}

var rupee = "\u20B9";

function fmtMoney(v) {
  var n = Number(v);
  if (isNaN(n)) return rupee + "0";
  return rupee + n.toFixed(2);
}

// Small pure helper so the cart sidebar can show the tax split before
// the order is actually created.  Assumes the menu prices are GST-
// inclusive (same convention the backend uses for vendor items).
function splitPrice(total) {
  var n = Number(total) || 0;
  // Menu prices are tax-inclusive; reverse-derive the 18% tax.
  var subtotal = n / 1.18;
  var tax = n - subtotal;
  // Round the payable to the nearest rupee so the cash box matches the
  // UI and we don't ask the customer for paise.  The delta lives on a
  // dedicated "Round off" line so the math still ties out.
  var payable = Math.round(n);
  var roundOff = payable - n;
  return {
    subtotal: subtotal,
    cgst: tax / 2,
    sgst: tax / 2,
    total: n,
    round_off: roundOff,
    payable: payable
  };
}

const StallOrderPage = function () {
  var qs = useQuery();
  var routeParams = useParams() || {};
  var slug = routeParams.slug || qs.get("slug") || "";
  var [eventId, setEventId] = useState(qs.get("event_id") || "");
  var [stallId, setStallId] = useState(qs.get("stall_id") || "");
  var bootOrderRef = qs.get("order_ref") || "";

  var [store, setStore] = useState({ loading: true, error: "", stall: null, items: [] });
  var [cart, setCart] = useState([]);
  var [cust, setCust] = useState({ customer_name: "", customer_mobile: "" });
  var [step, setStep] = useState("shop"); // shop | checkout | pay | paid_poll | done
  var [busy, setBusy] = useState("");
  var [err, setErr] = useState("");
  var [placed, setPlaced] = useState(null);
  var [paidOrder, setPaidOrder] = useState(null);
  var [search, setSearch] = useState("");
  var [sheetOpen, setSheetOpen] = useState(false);

  // ── Load menu ────────────────────────────────────────────────
  useEffect(function () {
    if (!slug && (!eventId || !stallId)) {
      setStore({ loading: false, error: "Missing event or stall in the link.", stall: null, items: [] });
      return;
    }
    var alive = true;
    setStore(function (s) { return Object.assign({}, s, { loading: true, error: "" }); });
    var req = slug
      ? vendorpayApi.stallStorefrontBySlug(slug)
      : vendorpayApi.stallStorefront(eventId, stallId);
    req
      .then(function (payload) {
        if (!alive) return;
        var stall = (payload && payload.stall) || null;
        // When arriving via /stall/:slug the legacy order-create flow
        // still needs event_id + stall_id — hydrate both from the
        // resolved stall so checkout works without extra round-trips.
        if (slug && stall) {
          if (stall.event_id) setEventId(String(stall.event_id));
          if (stall.id) setStallId(String(stall.id));
        }
        setStore({
          loading: false, error: "",
          stall: stall,
          items: (payload && payload.items) || []
        });
      })
      .catch(function (e) {
        if (!alive) return;
        setStore({
          loading: false,
          error: e.message || "Could not load menu",
          stall: null, items: []
        });
      });
    return function () { alive = false; };
  }, [slug, eventId, stallId]);

  // ── Resume a pending order (if deep-linked after payment redirect)
  useEffect(function () {
    if (!bootOrderRef) return;
    setStep("paid_poll");
    var alive = true;
    var tick = function (n) {
      if (n > 20 || !alive) return;
      vendorpayApi.lookupOrder({ ref: bootOrderRef }).then(function (payload) {
        if (!alive) return;
        var o = (payload && payload.order) || ((payload && payload.orders) || [])[0] || null;
        if (o && o.payment_status === "SUCCESS") {
          setPaidOrder(o); setStep("done");
        } else {
          setTimeout(function () { tick(n + 1); }, 2500);
        }
      }).catch(function () {
        setTimeout(function () { tick(n + 1); }, 3000);
      });
    };
    tick(0);
    return function () { alive = false; };
  }, [bootOrderRef]);

  // ── Cart mutations ───────────────────────────────────────────
  var addItem = function (it) {
    setCart(function (prev) {
      var idx = prev.findIndex(function (c) { return c.vendor_item_id === it.id; });
      if (idx === -1) {
        return prev.concat([{
          vendor_item_id: it.id,
          item_name: it.item_name,
          item_code: it.item_code || "",
          unit_price: Number(it.selling_price),
          quantity: 1
        }]);
      }
      var n = prev.slice();
      n[idx] = Object.assign({}, n[idx], { quantity: n[idx].quantity + 1 });
      return n;
    });
  };

  var bumpQty = function (id, delta) {
    setCart(function (prev) {
      return prev.map(function (c) {
        return c.vendor_item_id === id
          ? Object.assign({}, c, { quantity: Math.max(0, c.quantity + delta) })
          : c;
      }).filter(function (c) { return c.quantity > 0; });
    });
  };

  var itemQty = useCallback(function (id) {
    var row = cart.find(function (l) { return l.vendor_item_id === id; });
    return row ? row.quantity : 0;
  }, [cart]);

  var cartTotal = useMemo(function () {
    return cart.reduce(function (a, l) { return a + (l.unit_price || 0) * (l.quantity || 0); }, 0);
  }, [cart]);
  var cartCount = useMemo(function () {
    return cart.reduce(function (a, l) { return a + (l.quantity || 0); }, 0);
  }, [cart]);
  var split = useMemo(function () { return splitPrice(cartTotal); }, [cartTotal]);

  // ── Filtered menu based on search ────────────────────────────
  var filteredItems = useMemo(function () {
    var q = search.trim().toLowerCase();
    if (!q) return store.items;
    return store.items.filter(function (it) {
      return (it.item_name || "").toLowerCase().includes(q)
          || (it.item_code || "").toLowerCase().includes(q);
    });
  }, [store.items, search]);

  // ── Place & pay ──────────────────────────────────────────────
  var submitOrder = useCallback(function () {
    if (!store.stall || cart.length === 0) return;
    var name = (cust.customer_name || "").trim();
    var mobile = sanitizeIndianMobile10Input(cust.customer_mobile);
    if (!name) {
      setErr("Please enter the name for this order.");
      return;
    }
    if (!isIndianMobile10(mobile)) {
      setErr("Enter a valid 10-digit mobile number.");
      return;
    }
    setBusy("order"); setErr("");
    vendorpayApi.customerCreateOrder({
      event_id: eventId,
      stall_id: Number(stallId),
      vendor_id: store.stall.vendor_id,
      customer_name: name,
      customer_mobile: mobile,
      lines: cart.map(function (l) {
        return { vendor_item_id: l.vendor_item_id, quantity: l.quantity };
      }),
      payment_mode: "ONLINE"
    }).then(function (payload) {
      setPlaced(payload); setBusy(""); setStep("pay"); setSheetOpen(false);
    }).catch(function (e) {
      setErr(e.message || "Order failed"); setBusy("");
    });
  }, [store.stall, cart, cust, eventId, stallId]);

  var resendPaylink = useCallback(function () {
    if (!placed || !placed.order) return;
    setBusy("resend"); setErr("");
    vendorpayApi.customerPaylinkResend({
      order_reference_code: placed.order.order_reference_code,
      mobile: sanitizeIndianMobile10Input(cust.customer_mobile)
    }).then(function (r) {
      setPlaced(function (prev) {
        return Object.assign({}, prev, { pay_link: Object.assign({}, (prev && prev.pay_link) || {}, {
          short_url: r.short_url, sms_sent: r.sms_sent, mobile: r.mobile
        }) });
      });
      setBusy("");
    }).catch(function (e) {
      setErr(e.message || "Could not resend SMS"); setBusy("");
    });
  }, [placed, cust]);

  var payNow = useCallback(function () {
    if (!placed || !placed.order) return;
    setBusy("pay"); setErr("");
    loadCashfreeScript()
      .then(function () {
        return vendorpayApi.customerPaymentInit({
          order_reference_code: placed.order.order_reference_code
        });
      })
      .then(function (pg) {
        var mode = process.env.REACT_APP_CASHFREE_ENV === "sandbox" ? "sandbox" : "production";
        if (!window.Cashfree) {
          setErr("Payment SDK unavailable. Try again on a network that can reach Cashfree.");
          setBusy(""); return;
        }
        var cf = window.Cashfree({ mode: mode });
        return cf.checkout({
          paymentSessionId: pg.payment_session_id,
          redirectTarget: "_modal",
          appearance: { width: "425px", height: "700px" }
        }).then(function () {
          setStep("paid_poll"); setBusy("");
          var ref = placed.order.order_reference_code;
          var n = 0;
          var poll = function () {
            if (n++ > 24) return;
            vendorpayApi.lookupOrder({ ref: ref }).then(function (payload) {
              var o = (payload && payload.order) || ((payload && payload.orders) || [])[0] || null;
              if (o && o.payment_status === "SUCCESS") { setPaidOrder(o); setStep("done"); }
              else setTimeout(poll, 2500);
            }).catch(function () { setTimeout(poll, 3000); });
          };
          poll();
        });
      })
      .catch(function (e) { setErr(e.message || "Payment start failed"); setBusy(""); });
  }, [placed]);

  // ── Guards ───────────────────────────────────────────────────
  if (!eventId || !stallId) {
    return (
      <div className="so-page">
        <div className="so-container">
          <div className="so-stage">
            <div className="so-stage__icon so-stage__icon--pay">!</div>
            <h2 className="so-stage__title">Invalid link</h2>
            <p className="so-stage__sub">
              This stall menu link is incomplete. Please scan the QR again or contact the help desk.
            </p>
            <Link to="/" className="so-cta" style={{ display: "inline-flex", width: "auto", padding: "12px 22px" }}>
              Home
            </Link>
          </div>
        </div>
      </div>
    );
  }

  if (store.loading) {
    return (
      <div className="so-page">
        <div className="so-loader">
          <div className="so-spinner" />
          <p>Loading menu…</p>
        </div>
      </div>
    );
  }

  if (store.error || !store.stall) {
    return (
      <div className="so-page">
        <div className="so-container">
          <div className="so-stage">
            <h2 className="so-stage__title">Menu unavailable</h2>
            <p className="so-stage__sub">{store.error || "Stall not found."}</p>
            <Link to="/events" className="so-cta so-cta--ghost" style={{ width: "auto", padding: "12px 22px" }}>
              Browse events
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // ── Stage: pay ───────────────────────────────────────────────
  if (step === "pay" && placed && placed.order) {
    var po = placed.order;
    return (
      <div className="so-page">
        <div className="so-container">
          <div className="so-stage">
            <div className="so-stage__icon so-stage__icon--pay">₹</div>
            <h2 className="so-stage__title">Complete payment</h2>
            <p className="so-stage__sub">
              Order <strong>{po.order_reference_code}</strong>
            </p>
            <div className="so-totals" style={{ textAlign: "left", maxWidth: 320, margin: "0 auto 16px" }}>
              <div className="so-totals__row"><span>Subtotal</span><span>{fmtMoney(po.subtotal_amount)}</span></div>
              {po.cgst_amount ? (
                <div className="so-totals__row"><span>CGST ({po.cgst_percent}%)</span><span>{fmtMoney(po.cgst_amount)}</span></div>
              ) : null}
              {po.sgst_amount ? (
                <div className="so-totals__row"><span>SGST ({po.sgst_percent}%)</span><span>{fmtMoney(po.sgst_amount)}</span></div>
              ) : null}
              {Number(po.round_off_amount || 0) !== 0 ? (
                <>
                  <div className="so-totals__row"><span>Total</span><span>{fmtMoney(po.total_amount)}</span></div>
                  <div className="so-totals__row"><span>Round off</span><span>{fmtMoney(po.round_off_amount)}</span></div>
                  <div className="so-totals__row so-totals__row--total">
                    <span>Payable</span>
                    <span>{fmtMoney(po.payable_amount || po.total_amount)}</span>
                  </div>
                </>
              ) : (
                <div className="so-totals__row so-totals__row--total">
                  <span>Total</span>
                  <span>{fmtMoney(po.payable_amount || po.total_amount)}</span>
                </div>
              )}
            </div>
            {placed && placed.pay_link ? (
              <div className="so-paylink">
                {placed.pay_link.sms_sent ? (
                  <div className="so-paylink__ok">
                    <span className="so-paylink__icon">✉︎</span>
                    <div>
                      <div><strong>Payment link sent</strong></div>
                      <div className="so-paylink__sub">
                        SMS delivered to ••••{(placed.pay_link.mobile || "").slice(-4)}. Tap the link any time to pay.
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="so-paylink__warn">
                    We couldn't send the SMS automatically. You can pay below, or
                    <button type="button" className="so-link" onClick={resendPaylink} disabled={busy === "resend"}>
                      {busy === "resend" ? "resending…" : "retry SMS"}
                    </button>.
                  </div>
                )}
                {placed.pay_link.sms_sent ? (
                  <button
                    type="button"
                    className="so-link so-link--center"
                    onClick={resendPaylink}
                    disabled={busy === "resend"}
                  >
                    {busy === "resend" ? "Resending…" : "Resend SMS"}
                  </button>
                ) : null}
              </div>
            ) : null}
            {err ? <div className="so-error">{err}</div> : null}
            <button className="so-cta" onClick={payNow} disabled={busy === "pay"} style={{ maxWidth: 320, margin: "0 auto" }}>
              {busy === "pay" ? (<><span className="so-spinner" style={{ width: 16, height: 16, borderWidth: 2 }} />Opening gateway…</>) : "Pay now · " + fmtMoney(po.payable_amount || po.total_amount)}
            </button>
            <button className="so-cta so-cta--ghost" onClick={function () { setStep("shop"); }} style={{ maxWidth: 320, margin: "8px auto 0" }}>
              Back to menu
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ── Stage: polling after payment ─────────────────────────────
  if (step === "paid_poll") {
    return (
      <div className="so-page">
        <div className="so-container">
          <div className="so-stage">
            <div className="so-stage__icon so-stage__icon--wait"><span className="so-spinner" style={{ border: "3px solid rgba(37,99,235,0.3)", borderTopColor: "#2563eb" }} /></div>
            <h2 className="so-stage__title">Confirming payment…</h2>
            <p className="so-stage__sub">Please wait while we confirm your payment with the bank.</p>
          </div>
        </div>
      </div>
    );
  }

  // ── Stage: done ──────────────────────────────────────────────
  if (step === "done" && paidOrder) {
    return (
      <div className="so-page">
        <div className="so-container">
          <div className="so-stage">
            <div className="so-stage__icon">✓</div>
            <h2 className="so-stage__title">Payment successful</h2>
            <p className="so-stage__sub">
              Order <strong>{paidOrder.order_reference_code}</strong> · Paid {fmtMoney(paidOrder.total_amount)}
            </p>
            <p className="so-stage__sub">
              Show the pickup QR below at <strong>{store.stall.stall_name}</strong> to collect your items.
            </p>
            <OrderPaidBlock orderRef={paidOrder.order_reference_code} />
          </div>
        </div>
      </div>
    );
  }

  // ── Stage: shop / checkout ───────────────────────────────────
  var orderCard = (
    <div className={"so-order" + (sheetOpen ? " so-order--open" : "")}>
      <div className="so-order__grip" aria-hidden="true" />
      <div className="so-order__head">
        <h3 className="so-order__title">Your order</h3>
        <span className="so-order__count">{cartCount} {cartCount === 1 ? "item" : "items"}</span>
      </div>

      {cart.length === 0 ? (
        <div className="so-order__empty">Tap any item on the menu to add it to your order.</div>
      ) : (
        <>
          {cart.map(function (l) {
            return (
              <div key={l.vendor_item_id} className="so-line">
                <div>
                  <div className="so-line__name">{l.item_name}</div>
                  <div className="so-line__meta">{fmtMoney(l.unit_price)} each</div>
                </div>
                <div className="so-stepper">
                  <button type="button" onClick={function () { bumpQty(l.vendor_item_id, -1); }} aria-label="Decrease">−</button>
                  <span className="so-stepper__qty">{l.quantity}</span>
                  <button type="button" onClick={function () { bumpQty(l.vendor_item_id, 1); }} aria-label="Increase">+</button>
                </div>
                <div className="so-line__price">{fmtMoney(l.unit_price * l.quantity)}</div>
              </div>
            );
          })}

          <div className="so-totals">
            <div className="so-totals__row"><span>Subtotal</span><span>{fmtMoney(split.subtotal)}</span></div>
            <div className="so-totals__row"><span>CGST (9%)</span><span>{fmtMoney(split.cgst)}</span></div>
            <div className="so-totals__row"><span>SGST (9%)</span><span>{fmtMoney(split.sgst)}</span></div>
            {Math.abs(split.round_off) >= 0.01 ? (
              <>
                <div className="so-totals__row"><span>Total</span><span>{fmtMoney(split.total)}</span></div>
                <div className="so-totals__row"><span>Round off</span><span>{fmtMoney(split.round_off)}</span></div>
                <div className="so-totals__row so-totals__row--total"><span>Payable</span><span>{fmtMoney(split.payable)}</span></div>
              </>
            ) : (
              <div className="so-totals__row so-totals__row--total"><span>Total</span><span>{fmtMoney(split.payable)}</span></div>
            )}
          </div>

          {step === "shop" ? (
            <button type="button" className="so-cta" onClick={function () { setStep("checkout"); }} disabled={cart.length === 0}>
              Continue →
            </button>
          ) : (
            <>
              <div className="so-form">
                <div className="so-form__title">Contact details</div>
                <p className="so-form__sub">We'll SMS your payment link &amp; receipt here.</p>
                <div className="so-form__row">
                  <label htmlFor="so-name">Your name <span className="so-req">*</span></label>
                  <input
                    id="so-name"
                    value={cust.customer_name}
                    onChange={function (e) {
                      var v = e.target.value;
                      setCust(function (c) { return Object.assign({}, c, { customer_name: v }); });
                    }}
                    placeholder="Name for the order"
                    autoComplete="name"
                    required
                  />
                </div>
                <div className="so-form__row">
                  <label htmlFor="so-mobile">Mobile number <span className="so-req">*</span></label>
                  <input
                    id="so-mobile"
                    type="tel"
                    inputMode="numeric"
                    maxLength={10}
                    value={cust.customer_mobile}
                    onChange={function (e) {
                      var v = sanitizeIndianMobile10Input(e.target.value);
                      setCust(function (c) { return Object.assign({}, c, { customer_mobile: v }); });
                    }}
                    placeholder="10-digit mobile"
                    autoComplete="tel"
                    required
                  />
                </div>
                {err ? <div className="so-form__err">{err}</div> : null}
              </div>

              <button
                type="button"
                className="so-cta"
                disabled={
                  busy === "order" ||
                  cart.length === 0 ||
                  !(cust.customer_name || "").trim() ||
                  !isIndianMobile10(cust.customer_mobile)
                }
                onClick={submitOrder}
              >
                {busy === "order" ? "Placing…" : "Place order · Pay " + fmtMoney(split.payable)}
              </button>
              <button type="button" className="so-cta so-cta--ghost" onClick={function () { setStep("shop"); }}>
                ← Back to menu
              </button>
            </>
          )}
        </>
      )}
    </div>
  );

  return (
    <div className="so-page">
      <div className="so-container">
        <div className="so-hero">
          <Link to="/events" className="so-hero__back">← Events</Link>
          <div className="so-hero__meta">
            <h1 className="so-hero__title">{store.stall.stall_name}</h1>
            <p className="so-hero__sub">
              {store.stall.vendor_name}
              {store.stall.event_title ? " · " + store.stall.event_title : (" · " + eventId)}
            </p>
          </div>
          <span className="so-hero__badge">Live ordering</span>
        </div>

        {err ? <div className="so-error">{err}</div> : null}

        <div className="so-grid">
          <div>
            <div className="so-toolbar">
              <div className="so-search">
                <span className="so-search__icon" aria-hidden="true">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="7"/><path d="m21 21-4.3-4.3"/></svg>
                </span>
                <input
                  type="search"
                  value={search}
                  onChange={function (e) { setSearch(e.target.value); }}
                  placeholder="Search the menu…"
                />
              </div>
              {search ? (
                <button type="button" className="so-chip" onClick={function () { setSearch(""); }}>Clear</button>
              ) : null}
            </div>

            <div className="so-menu">
              <div className="so-menu__title">
                <span>Menu</span>
                <span className="so-menu__count">{filteredItems.length} item{filteredItems.length === 1 ? "" : "s"}</span>
              </div>

              {store.items.length === 0 ? (
                <div className="so-empty">
                  <strong>No items yet</strong>
                  The stall hasn't listed any items. Check back soon.
                </div>
              ) : filteredItems.length === 0 ? (
                <div className="so-empty">
                  <strong>No matches</strong>
                  Nothing matched "<em>{search}</em>". Try a different keyword.
                </div>
              ) : (
                <div className="so-menu__grid">
                  {filteredItems.map(function (it) {
                    var qty = itemQty(it.id);
                    return (
                      <div key={it.id} className={"so-item" + (qty > 0 ? " so-item--active" : "")}>
                        <div className="so-item__code">{it.item_code || ""}</div>
                        <h4 className="so-item__name">{it.item_name}</h4>
                        <div className="so-item__footer">
                          <div className="so-item__price">{fmtMoney(it.selling_price)}</div>
                          {qty > 0 ? (
                            <div className="so-stepper">
                              <button type="button" onClick={function () { bumpQty(it.id, -1); }} aria-label="Decrease">−</button>
                              <span className="so-stepper__qty">{qty}</span>
                              <button type="button" onClick={function () { bumpQty(it.id, 1); }} aria-label="Increase">+</button>
                            </div>
                          ) : (
                            <button type="button" className="so-item__add" onClick={function () { addItem(it); }}>
                              + Add
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          {/* Desktop sidebar — hidden via CSS on narrow viewports */}
          {orderCard}
        </div>

        {/* Mobile floating cart bar */}
        {cartCount > 0 && !sheetOpen ? (
          <button
            type="button"
            className="so-mobar"
            onClick={function () { setSheetOpen(true); }}
          >
            <span className="so-mobar__count">{cartCount} in cart</span>
            <span>View order</span>
            <span className="so-mobar__total">{fmtMoney(split.payable)}</span>
          </button>
        ) : null}

        {/* Backdrop for the mobile sheet */}
        {sheetOpen ? (
          <div
            onClick={function () { setSheetOpen(false); }}
            style={{
              position: "fixed", inset: 0, background: "rgba(15, 23, 42, 0.35)",
              zIndex: 40, backdropFilter: "blur(2px)"
            }}
          />
        ) : null}
      </div>
    </div>
  );
};

/** Fetch receipt QR after success (lookup returns order + receipt). */
var OrderPaidBlock = function (props) {
  var ref = props.orderRef;
  var [st, setSt] = useState({ loading: true, receipt: null, order: null });
  useEffect(function () {
    vendorpayApi.lookupOrder({ ref: ref }).then(function (payload) {
      var o = (payload && payload.order) || null;
      var r = (payload && payload.receipt) || null;
      setSt({ loading: false, receipt: r, order: o });
    }).catch(function () {
      setSt({ loading: false, receipt: null, order: null });
    });
  }, [ref]);
  if (st.loading) {
    return (
      <div style={{ display: "flex", gap: 8, justifyContent: "center", alignItems: "center", color: "#64748b" }}>
        <span className="so-spinner" style={{ width: 16, height: 16, borderWidth: 2 }} /> Loading receipt…
      </div>
    );
  }
  var qr = st.receipt && (st.receipt.qr_url || st.receipt.qr_image_url);
  if (!qr) return null;
  return (
    <div className="so-stage__qr">
      <img src={qr} alt="Pickup QR" />
    </div>
  );
};

export default StallOrderPage;
